import React from 'react'
import { pantryColors } from '@pantry/ui'
import { specColors } from '../../constants'
import type { CannabinoidEntry, CannabinoidName, CannabinoidUnit } from '../../../../types/product'

const UNIT_OPTIONS: { label: string; value: CannabinoidUnit }[] = [
  { label: '%',       value: '%' },
  { label: 'mg',      value: 'mg' },
  { label: 'ml',      value: 'ml' },
  { label: 'mg/ml',   value: 'mg/ml' },
  { label: 'mg/unit', value: 'mg/unit' },
]

export const ALL_CANNABINOIDS_HIDDEN_TEXT = 'All cannabinoids are hidden — use “Add field” to show them again.'

interface CannabinoidTableProps {
  cannabinoids: CannabinoidEntry[]
  onChange: (name: CannabinoidEntry['name'], patch: Partial<CannabinoidEntry>) => void
  disabled?: boolean
  // Rows to render (all when omitted). Hiding never touches the values.
  visibleNames?: readonly CannabinoidName[]
  // Shows a hide icon column; called with the row's cannabinoid.
  onHide?: (name: CannabinoidName) => void
  // Extra content under a row (e.g. the ◆ per-variant diff line).
  renderBelowRow?: (name: CannabinoidName) => React.ReactNode
  // Shown instead of the table when no row is visible; null renders nothing.
  emptyText?: React.ReactNode
}

const colHeaderStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '.5px',
  color: pantryColors.gray[400],
}

const inputStyle = (disabled?: boolean): React.CSSProperties => ({
  width: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
  border: `1px solid ${pantryColors.gray[200]}`,
  borderRadius: 6,
  padding: '7px 6px',
  fontSize: 13,
  fontFamily: 'inherit',
  color: pantryColors.gray[800],
  background: disabled ? pantryColors.gray[50] : pantryColors.white,
  outline: 'none',
  textAlign: 'center' as const,
})

const selectStyle = (disabled?: boolean): React.CSSProperties => ({
  width: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
  border: `1px solid ${pantryColors.gray[200]}`,
  borderRadius: 6,
  padding: '7px 4px',
  fontSize: 12,
  fontFamily: 'inherit',
  color: pantryColors.gray[800],
  background: disabled ? pantryColors.gray[50] : pantryColors.white,
  outline: 'none',
  cursor: disabled ? 'default' : 'pointer',
})

const gridStyle = (withHide: boolean): React.CSSProperties => ({
  display: 'grid',
  gridTemplateColumns: withHide
    ? '80px minmax(0, 1fr) minmax(0, 1fr) 90px 28px'
    : '80px minmax(0, 1fr) minmax(0, 1fr) 90px',
  gap: 8,
  alignItems: 'center',
})

const hideIconButtonStyle = (disabled?: boolean): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  background: 'none',
  padding: 5,
  borderRadius: 4,
  color: pantryColors.gray[400],
  cursor: disabled ? 'default' : 'pointer',
})

export const HideIcon: React.FC<{ size?: number }> = ({ size = 12 }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: size, height: size }} aria-hidden>
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
)

export const CannabinoidTable: React.FC<CannabinoidTableProps> = ({
  cannabinoids,
  onChange,
  disabled,
  visibleNames,
  onHide,
  renderBelowRow,
  emptyText = ALL_CANNABINOIDS_HIDDEN_TEXT,
}) => {
  const rows = visibleNames ? cannabinoids.filter((c) => visibleNames.includes(c.name)) : cannabinoids
  const withHide = Boolean(onHide)

  if (rows.length === 0) {
    if (emptyText == null) return null
    return (
      <div style={{ fontSize: 12, color: pantryColors.gray[400], fontStyle: 'italic', padding: '8px 0' }}>
        {emptyText}
      </div>
    )
  }

  return (
    <div>
      <div
        style={{
          ...gridStyle(withHide),
          paddingBottom: 6,
          borderBottom: `1px solid ${pantryColors.gray[200]}`,
          marginBottom: 2,
        }}
      >
        <span style={colHeaderStyle}>Cannabinoid</span>
        <span style={{ ...colHeaderStyle, textAlign: 'center' }}>Min</span>
        <span style={{ ...colHeaderStyle, textAlign: 'center' }}>Max</span>
        <span style={{ ...colHeaderStyle, textAlign: 'center' }}>Unit</span>
        {withHide && <span />}
      </div>

      {rows.map((c, idx) => (
        <React.Fragment key={c.name}>
          <div
            data-cannabinoid-row={c.name}
            style={{
              ...gridStyle(withHide),
              padding: '7px 0',
              borderBottom: idx < rows.length - 1 ? `1px solid ${pantryColors.gray[50]}` : 'none',
            }}
          >
            <span
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: pantryColors.gray[800],
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: specColors.cannabinoid[c.name] ?? pantryColors.gray[400],
                  flexShrink: 0,
                  display: 'inline-block',
                }}
              />
              {c.name}
            </span>

            <input
              type="number"
              value={c.min ?? ''}
              min={0}
              max={100}
              step={0.1}
              disabled={disabled}
              onChange={(e) => onChange(c.name, { min: e.target.value === '' ? undefined : Number(e.target.value) })}
              style={inputStyle(disabled)}
              placeholder="—"
              aria-label={`${c.name} min`}
            />

            <input
              type="number"
              value={c.max ?? ''}
              min={0}
              max={100}
              step={0.1}
              disabled={disabled}
              onChange={(e) => onChange(c.name, { max: e.target.value === '' ? undefined : Number(e.target.value) })}
              style={inputStyle(disabled)}
              placeholder="—"
              aria-label={`${c.name} max`}
            />

            <select
              value={c.unit ?? '%'}
              disabled={disabled}
              onChange={(e) => onChange(c.name, { unit: e.target.value as CannabinoidUnit })}
              style={selectStyle(disabled)}
              aria-label={`${c.name} unit`}
            >
              {UNIT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>

            {onHide && (
              <button
                type="button"
                onClick={() => onHide(c.name)}
                disabled={disabled}
                title={`Hide ${c.name}`}
                aria-label={`Hide ${c.name}`}
                style={hideIconButtonStyle(disabled)}
              >
                <HideIcon />
              </button>
            )}
          </div>
          {renderBelowRow?.(c.name)}
        </React.Fragment>
      ))}
    </div>
  )
}
